import { Chart } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import {
  AnimationEvent,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  ScriptableScaleContext,
  SubTitle,
  Title,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { isNumber } from 'lodash';
import { IAreaChartData } from 'PDF/components/Charts/AreaChart/types';
import { formatUnit } from 'PDF/helpers/chartFormatters';
import { checkIsEmptyData } from 'PDF/helpers/pdfMapping';

import { calculatePadding, splitLabel } from 'helpers/charts';
import { formatNumber } from 'helpers/numbers';

import { emptyChart } from '../types';

ChartJS.register(
  LinearScale,
  CategoryScale,
  PointElement,
  LineElement,
  Legend,
  LineController,
  Title,
  SubTitle,
  Filler,
  ChartDataLabels,
);

export type IAreaChartProps = {
  handleOnComplete: (uri: string) => void;
  info: IAreaChartData;
  aspectRatio: number;
  stacked?: boolean;
  showDataLabels?: boolean;
  axiosInPercentageProps?: {
    min: number;
    max: number;
    stepSize: number;
  };
};

ChartJS.defaults.font.family = 'Noto Sans';

const labelMainProperties = {
  font: {
    size: 26,
    weight: 300,
  },
  color: '#77787C',
};

const barMainProperties = {
  borderWidth: 2,
  fill: 'start',
};

export const AreaChart = ({
  info: { source, data, datasetsLabels, labels, unit = '', colors },
  aspectRatio,
  handleOnComplete,
  stacked,
  showDataLabels = false,
}: IAreaChartProps) => {
  const { t } = useTranslation();
  const allValues: string[] = [
    ...data.flatMap((dataset) => dataset.map((value) => (isNumber(value) ? Math.ceil(value).toString() : value))),
  ];

  const chartData: ChartData = {
    labels,
    datasets: data.map((item, index) => ({
      label: datasetsLabels?.[index],
      backgroundColor: colors[index],
      data: item,
      ...barMainProperties,
    })),
  };

  const plugins = [
    {
      id: 'unitPlugin',
      afterDraw(chart: ChartJS) {
        const ctx = chart.ctx;
        const leftX = chart.chartArea?.left;

        ctx.fillStyle = labelMainProperties.color;
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.font = '300 24px Noto Sans';

        ctx.fillText(formatUnit(unit), leftX - 18, 20);
      },
    },
  ];

  const options: ChartOptions = {
    aspectRatio,
    devicePixelRatio: 1,
    layout: {
      padding: {
        left: calculatePadding(allValues, unit),
      },
    },
    elements: {
      point: {
        radius: 0,
      },
    },

    plugins: {
      title: {
        display: Boolean(unit),
        text: '',
        align: 'start',
        padding: { bottom: 30 },
        font: {
          size: 24,
          weight: 400,
        },
        color: labelMainProperties.color,
      },
      subtitle: {
        display: Boolean(source),
        text: `${t('source')}: ${source}`,
        position: 'bottom',
        align: 'start',
        ...labelMainProperties,
      },
      legend: {
        display: Boolean(datasetsLabels),
        position: 'bottom',
        align: 'center',
        labels: {
          boxWidth: 26,
          boxHeight: 26,
          padding: 30,
          ...labelMainProperties,
        },
      },
      datalabels: {
        display: Boolean(showDataLabels),
        color: colors[0],
        anchor: 'end',
        align: 'end',
        font: {
          weight: 600,
          size: 26,
        },
        formatter: (value: string) => formatNumber(+value, Number.isInteger(+value) ? 0 : 1),
      },
    },
    scales: {
      y: {
        display: true,
        beginAtZero: true,
        ticks: {
          color: '#77787C',
          font: {
            size: 26,
            weight: 300,
          },
          padding: 10,
        },
        grid: {
          color: (ctx: ScriptableScaleContext) => (ctx.tick.value === 0 ? '#77787C' : 'transparent'),
          tickBorderDash: [3, 7],
        },
        border: { display: false, dash: [3, 7] },
        stacked: Boolean(stacked),
      },
      x: {
        display: true,
        offset: true,
        ticks: {
          ...labelMainProperties,
          maxRotation: 0,
          callback: function (_: unknown, index: number) {
            return splitLabel(labels[index].toString());
          },
        },
        grid: {
          color: 'transparent',
        },
        border: { display: false, color: 'transparent' },
        stacked: Boolean(stacked),
      },
    },
    animation: {
      duration: 0,
      onComplete(event: AnimationEvent) {
        handleOnComplete(checkIsEmptyData(data) ? emptyChart : event.chart.toBase64Image());
      },
    },
  };

  return <Chart type="line" data={chartData} options={options} plugins={plugins} />;
};
